if (!customElements.get('rs7gt-launch-floating')) {
  customElements.define('rs7gt-launch-floating', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.launchRoot = this.closest('rs7gt-launch');
      this.scene = this.querySelector('[data-floating-scene]');
      this.stage = this.querySelector('[data-floating-stage]');
      this.status = this.querySelector('[data-floating-status]');
      this.buttons = [...this.querySelectorAll('[data-floating-select]')];
      this.entries = [...this.querySelectorAll('[data-floating-slide]')].map(slide => ({
        slide, image: slide.querySelector('[data-floating-image]'), caption: slide.querySelector('[data-clock-manual]')
      }));
      if (!this.scene || !this.stage || !this.entries.length) return;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.currentStep = 0;
      this.desiredStep = 0;
      this.selection = 0;
      this.near = false;
      this.inView = false;
      if (this.status) this.status.textContent = '';
      this.setAttribute('data-floating-ready', '');
      this.commit(0, { instant: true });
      const options = { signal: this.controller.signal };
      this.buttons.forEach(button => {
        button.addEventListener('click', () => this.select(Number(button.dataset.floatingSelect)), options);
        button.addEventListener('keydown', event => {
          if (event.altKey || event.ctrlKey || event.metaKey || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          event.stopPropagation();
          const selected = event.key === 'Home' ? 0 : event.key === 'End' ? this.entries.length - 1
            : Number(button.dataset.floatingSelect) + (event.key === 'ArrowRight' ? 1 : -1);
          const index = Math.max(0, Math.min(this.entries.length - 1, selected));
          this.buttons[index]?.focus({ preventScroll: true });
          this.select(index);
        }, options);
      });
      this.motionQuery.addEventListener('change', () => this.updateMotion(), options);
      this.launchRoot?.addEventListener('rs7gt:motionchange', () => {
        cancelAnimationFrame(this.motionFrame);
        this.motionFrame = requestAnimationFrame(() => {
          this.motionFrame = 0;
          this.updateMotion();
          this.launchRoot?.pager?.syncFloating({ instant: true });
        });
      }, options);
      this.launchRoot?.addEventListener('rs7gt:pagecoveragechange', () => this.updateVisibility(), options);
      document.addEventListener('visibilitychange', () => this.updateVisibility(), options);
      document.addEventListener('shopify:block:select', event => {
        const index = this.entries.findIndex(entry => entry.slide.contains(event.target)
          || entry.slide.dataset.floatingBlock === event.detail?.blockId);
        if (index >= 0) this.select(index, true);
      }, options);
      if ('IntersectionObserver' in window) {
        this.preloadObserver = new IntersectionObserver(entries => {
          this.near = entries[0].isIntersecting;
          if (this.near) this.setStep(this.desiredStep, { instant: !this.inView, load: true });
        }, { rootMargin: '300px 0px' });
        this.preloadObserver.observe(this.stage);
        this.viewObserver = new IntersectionObserver(entries => {
          this.inView = entries[0].intersectionRatio >= 0.01;
          this.updateVisibility();
        }, { threshold: [0, 0.01] });
        this.viewObserver.observe(this.stage);
      } else {
        this.near = this.inView = true;
        this.setStep(0, { instant: true, load: true });
      }
      this.updateMotion();
      this.launchRoot?.pager?.syncFloating({ instant: true });
      this.layoutFrame = requestAnimationFrame(() => {
        this.layoutFrame = 0;
        if (!options.signal.aborted) this.launchRoot?.refresh?.();
      });
    }

    select(index, instant = false) {
      const pager = this.launchRoot?.pager;
      const target = pager?.stops.findIndex(stop => stop.element === this.scene && stop.floatingStep === index) ?? -1;
      if (target >= 0) pager.go(target, { instant });
      else this.setStep(index, { instant, load: true });
    }

    setStep(index, { instant = false, direction = 0, load = false } = {}) {
      if (!this.controller || !this.entries?.length) return;
      const next = Math.max(0, Math.min(this.entries.length - 1, Number(index) || 0));
      const changed = this.desiredStep !== next;
      this.desiredStep = next;
      if (!changed && (this.pendingStep === next || (this.currentStep === next && this.entries[next].loaded))) return;
      const token = ++this.selection;
      const signal = this.controller.signal;
      this.pendingStep = null;
      this.scene.removeAttribute('aria-busy');
      if (!this.near && !this.inView && !load) return;
      this.pendingStep = next;
      this.scene.setAttribute('aria-busy', 'true');
      this.loadImage(next).then(loaded => {
        if (signal.aborted || !this.isConnected || token !== this.selection) return;
        this.pendingStep = null;
        this.scene.removeAttribute('aria-busy');
        this.toggleAttribute('data-floating-error', !loaded);
        if (!loaded) return;
        this.commit(next, { instant, direction });
        if (this.near && next + 1 < this.entries.length) this.loadImage(next + 1);
      });
    }

    loadImage(index) {
      const entry = this.entries[index];
      if (!entry?.image) return Promise.resolve(false);
      if (entry.loaded) return Promise.resolve(true);
      if (entry.promise) return entry.promise;
      const image = entry.image;
      const signal = this.controller.signal;
      image.loading = 'eager';
      if (image.dataset.floatingSrcset) image.srcset = image.dataset.floatingSrcset;
      if (image.dataset.floatingSrc) image.src = image.dataset.floatingSrc;
      entry.promise = new Promise(resolve => {
        let settled = false;
        const finish = async () => {
          if (settled) return;
          settled = true;
          image.removeEventListener('load', finish);
          image.removeEventListener('error', finish);
          signal.removeEventListener('abort', finish);
          clearTimeout(timeout);
          if (!signal.aborted && image.naturalWidth) {
            try { await image.decode(); } catch (_) { /* The loaded image remains the visual fallback. */ }
          }
          entry.loaded = !signal.aborted && image.naturalWidth > 0;
          resolve(entry.loaded);
        };
        const timeout = setTimeout(finish, 15000);
        image.addEventListener('load', finish, { once: true });
        image.addEventListener('error', finish, { once: true });
        signal.addEventListener('abort', finish, { once: true });
        if (image.complete && image.currentSrc) finish();
      }).then(loaded => {
        entry.promise = null;
        return loaded;
      });
      return entry.promise;
    }

    commit(index, { instant = false, direction = 0 } = {}) {
      const changed = this.currentStep !== index;
      this.currentStep = index;
      this.toggleAttribute('data-floating-instant', instant || !this.motionEnabled);
      this.style.setProperty('--rs7-floating-direction', String(direction || 1));
      this.entries.forEach((entry, step) => {
        entry.slide.classList.toggle('is-current', step === index);
        entry.slide.setAttribute('aria-hidden', String(step !== index));
        entry.slide.toggleAttribute('inert', step !== index);
      });
      this.buttons.forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.floatingSelect) === index)));
      if (changed && this.status && this.isSceneVisible()) this.status.textContent = this.entries[index].slide.dataset.floatingName;
      this.dataset.floatingIndex = String(index);
      this.revealCaption();
    }

    updateMotion() {
      if (!this.controller) return;
      this.motionEnabled = !this.motionQuery.matches && this.dataset.designMode !== 'true'
        && this.launchRoot?.dataset.designMode !== 'true' && !this.launchRoot?.hasAttribute('data-motion-paused');
      this.toggleAttribute('data-floating-motion', this.motionEnabled);
      this.updateVisibility();
    }

    updateVisibility() {
      this.toggleAttribute('data-floating-in-view', this.isSceneVisible());
      this.revealCaption();
    }

    isSceneVisible() {
      return this.inView && !document.hidden && (this.launchRoot?.sceneVisibility?.(this) ?? 1) > 0;
    }

    revealCaption() {
      if (!this.isSceneVisible() || !this.motionEnabled || this.textFrame) return;
      this.textFrame = requestAnimationFrame(() => {
        this.textFrame = 0;
        if (this.isConnected && this.isSceneVisible() && this.motionEnabled) {
          const caption = this.entries[this.currentStep]?.caption;
          if (!caption) return;
          const bounds = caption.getBoundingClientRect();
          if (bounds.bottom <= 0 || bounds.top >= window.innerHeight
            || bounds.right <= 0 || bounds.left >= window.innerWidth
            || getComputedStyle(caption).visibility === 'hidden') return;
          this.launchRoot?.clockReveal?.reveal(caption);
        }
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.preloadObserver?.disconnect();
      this.viewObserver?.disconnect();
      cancelAnimationFrame(this.motionFrame);
      cancelAnimationFrame(this.textFrame);
      cancelAnimationFrame(this.layoutFrame);
      this.motionFrame = this.textFrame = this.layoutFrame = 0;
      this.pendingStep = null;
      ['data-floating-ready', 'data-floating-in-view', 'data-floating-motion', 'data-floating-instant', 'data-floating-error'].forEach(name => this.removeAttribute(name));
      this.scene?.removeAttribute('aria-busy');
      this.entries?.forEach(entry => {
        entry.slide.removeAttribute('aria-hidden');
        entry.slide.removeAttribute('inert');
      });
    }
  });
}
